import { Eyebrow } from '@/beta/components/Eyebrow';
import { TierBadge } from '@/beta/components/TierBadge';
import type { CatalogVehicle } from '@/beta/types/catalog';

export interface ChapterCardProps {
  /** 1-based index used in the "No. ##" eyebrow. */
  index: number;
  vehicle: CatalogVehicle;
  /** Fired on click — caller navigates. */
  onActivate: (vehicleId: string) => void;
  /** Fired on pointerdown — caller calls queryClient.prefetchQuery. */
  onPrefetch: (vehicleId: string) => void;
}

function pad2(n: number) { return n < 10 ? `0${n}` : String(n); }

function initials(name: string): string {
  // First letter of first two words, uppercased. "Jaguar E-Type" → "JE".
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function spanText(vehicle: CatalogVehicle): string {
  const { from, to, location } = vehicle.ownershipSpan;
  const range = from && to ? `${from} → ${to}` : from ? `${from}` : '';
  return location ? `${range} · ${location}` : range;
}

/** Card for the Catalog grid. Spec § 6.1 chapter card. */
export function ChapterCard({ index, vehicle, onActivate, onPrefetch }: ChapterCardProps) {
  const { name, heroImageUrl, tier } = vehicle;

  return (
    <button
      type="button"
      onPointerDown={() => onPrefetch(vehicle.id)}
      onClick={() => onActivate(vehicle.id)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--vm-dark-card)',
        color: 'var(--vm-warm-white)',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
      data-vm-chapter-card
    >
      {/* Hero band — 3:2 aspect */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '3 / 2',
          background: 'var(--vm-charcoal)',
          overflow: 'hidden',
        }}
      >
        {heroImageUrl ? (
          <img
            src={heroImageUrl}
            alt={name}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 64,
              letterSpacing: '0.1em',
              color: 'var(--vm-stone)',
            }}
          >
            {initials(name)}
          </div>
        )}

        {/* Top-left eyebrow */}
        <div style={{ position: 'absolute', top: 'var(--vm-s-2)', left: 'var(--vm-s-2)' }}>
          <Eyebrow tone="red">No. {pad2(index)}</Eyebrow>
        </div>

        {/* Top-right tier badge */}
        <div style={{ position: 'absolute', top: 'var(--vm-s-2)', right: 'var(--vm-s-2)' }}>
          <TierBadge tier={tier} />
        </div>
      </div>

      {/* Footer panel */}
      <div
        style={{
          padding: 'var(--vm-s-3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--vm-s-2)',
          borderTop: '1px solid var(--vm-border)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--vm-font-heading)',
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.1,
            textTransform: 'none',
            color: 'var(--vm-warm-white)',
          }}
        >
          {name}
        </h2>

        <div
          style={{
            fontFamily: 'var(--vm-font-body)',
            fontSize: 11,
            lineHeight: 1.3,
            letterSpacing: '0.05em',
            color: 'var(--vm-stone-light)',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {spanText(vehicle)}
        </div>
      </div>
    </button>
  );
}
